import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar } from "@/components/AppTopBar";
import { SkuCombobox } from "@/components/SkuCombobox";

const CHANNELS = [
  { code: "WHATSAPP", label: "WhatsApp" },
  { code: "OFFLINE", label: "Offline / Langsung" },
  { code: "RESELLER", label: "Reseller / Grosir" },
  { code: "OTHER", label: "Lainnya" },
];
const channelLabel = (c) => (CHANNELS.find((x) => x.code === c) || {}).label || c;
const idr = (v) => "Rp " + Number(v || 0).toLocaleString("id-ID");
const uomLabel = (u) => (u.label && u.label !== u.uom_code ? `${u.uom_code} (${u.label})` : u.uom_code);
const CTYPE = { RETAIL: "Retail", RESELLER: "Reseller", WHOLESALE: "Grosir", OTHER: "Lainnya" };

export default function ManualSales() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canView = user && (user.role === "OWNER" || user.role === "ADMIN_INVENTORY");
  const isOwner = user?.role === "OWNER";
  const [list, setList] = useState([]);
  const [current, setCurrent] = useState(null);
  const [products, setProducts] = useState([]);
  const [invMap, setInvMap] = useState({});
  const [head, setHead] = useState({ channel: "WHATSAPP", customer_name: "", note: "", customer_id: "" });
  const [customers, setCustomers] = useState([]);
  const [item, setItem] = useState({ product_id: "", uom_code: "", uom_qty: "", unit_selling_price: "" });
  const [uoms, setUoms] = useState([]); // active uoms for selected product
  const [baseUom, setBaseUom] = useState("PCS");
  const [sellAvail, setSellAvail] = useState(null);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");
  const [pay, setPay] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [showPayForm, setShowPayForm] = useState(false);
  const [payForm, setPayForm] = useState({ amount: "", account_id: "", reference: "" });

  const loadList = useCallback(() => {
    api.get("/inventory/manual-sales").then(({ data }) => setList(data)).catch(() => {});
    api.get("/products").then(({ data }) => setProducts(data.filter((p) => p.status === "ACTIVE"))).catch(() => {});
    api.get("/inventory").then(({ data }) => {
      const m = {}; data.forEach((r) => { m[r.product_id || r.id] = r.available_qty; }); setInvMap(m);
    }).catch(() => {});
    api.get("/customers?status=ACTIVE").then(({ data }) => setCustomers(data)).catch(() => {});
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  // Load active UOMs whenever the selected product changes.
  const loadUoms = useCallback((productId, keepUom) => {
    if (!productId) { setUoms([]); setBaseUom("PCS"); return; }
    api.get(`/products/${productId}/uoms`).then(({ data }) => {
      const active = (data.uoms || []).filter((u) => u.is_active);
      setUoms(active); setBaseUom(data.base_uom || "PCS");
      setItem((prev) => {
        const has = active.some((u) => u.uom_code === prev.uom_code);
        if (keepUom && has) return prev;
        return { ...prev, uom_code: data.base_uom || (active[0] && active[0].uom_code) || "" };
      });
    }).catch(() => { setUoms([]); setBaseUom("PCS"); });
  }, []);

  const openSale = (id) => api.get(`/inventory/manual-sales/${id}`).then(({ data }) => {
    setCurrent(data);
    setShowPayForm(false);
    setPayForm({ amount: "", account_id: "", reference: "" });
    if (isOwner && data.status === "POSTED") {
      api.get(`/inventory/manual-sales/${id}/payments`).then(({ data: pd }) => setPay(pd)).catch(() => setPay(null));
      api.get(`/accounts`).then(({ data: ad }) => setAccounts(ad)).catch(() => {});
    } else { setPay(null); }
  }).catch(() => {});

  const reloadPay = () => api.get(`/inventory/manual-sales/${current.id}/payments`).then(({ data }) => setPay(data)).catch(() => {});

  const recordPayment = async () => {
    setError("");
    const amount = parseFloat(payForm.amount || "0");
    if (amount <= 0) { setError("Nominal pembayaran harus lebih dari 0."); return; }
    if (!payForm.account_id) { setError("Pilih account penerima."); return; }
    try {
      const { data } = await api.post(`/inventory/manual-sales/${current.id}/payments`, {
        amount, account_id: payForm.account_id, reference: payForm.reference || null,
        idempotency_key: `CPAY-${current.id}-${Date.now()}`,
      });
      setPay(data);
      setShowPayForm(false);
      setPayForm({ amount: "", account_id: "", reference: "" });
      api.get(`/accounts`).then(({ data: ad }) => setAccounts(ad)).catch(() => {});
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // Composition-aware sellable availability for the selected product (parent GOOD, or derived
  // from component GOOD stocks when the SKU has an Inventory Composition).
  const loadAvail = useCallback((productId) => {
    if (!productId) { setSellAvail(null); return; }
    api.get(`/inventory/availability/${productId}`).then(({ data }) => setSellAvail(data.available_qty))
      .catch(() => setSellAvail(null));
  }, []);

  const createDraft = async () => {
    setError("");
    try {
      const body = { channel: head.channel, note: head.note };
      if (head.customer_id) body.customer_id = head.customer_id;
      else if (head.customer_name) body.customer_name = head.customer_name;
      const { data } = await api.post("/inventory/manual-sales", body); setCurrent(data); loadList();
    }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const changeCustomer = async (customerId) => {
    setError("");
    try {
      const { data } = await api.patch(`/inventory/manual-sales/${current.id}`, { customer_id: customerId || null });
      setCurrent(data);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openOrCreateDO = async () => {
    setError("");
    if (current.delivery_order_id) { navigate(`/inventory/delivery-orders/${current.delivery_order_id}`); return; }
    try {
      const { data } = await api.post(`/inventory/manual-sales/${current.id}/delivery-order`, {});
      navigate(`/inventory/delivery-orders/${data.id}`);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openOrCreateInvoice = async () => {
    setError("");
    if (current.invoice_id) { navigate(`/inventory/invoices/${current.invoice_id}`); return; }
    try {
      const { data } = await api.post(`/inventory/manual-sales/${current.id}/invoice`);
      navigate(`/inventory/invoices/${data.id}`);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const onPickProduct = (pid) => { setItem({ ...item, product_id: pid, uom_code: "" }); loadUoms(pid, false); loadAvail(pid); };

  const addItem = async () => {
    setError("");
    if (!item.product_id || !item.uom_code || item.uom_qty === "" || item.unit_selling_price === "") {
      setError("SKU, satuan, qty, dan harga jual wajib diisi."); return;
    }
    try {
      if (editId) {
        const { data } = await api.patch(`/inventory/manual-sales/${current.id}/items/${editId}`, {
          uom_code: item.uom_code, uom_qty: parseInt(item.uom_qty, 10),
          unit_selling_price: parseFloat(item.unit_selling_price),
        });
        setCurrent(data); cancelEdit();
      } else {
        const { data } = await api.post(`/inventory/manual-sales/${current.id}/items`, {
          product_id: item.product_id, uom_code: item.uom_code, uom_qty: parseInt(item.uom_qty, 10),
          unit_selling_price: parseFloat(item.unit_selling_price),
        });
        setCurrent(data); setItem({ product_id: "", uom_code: "", uom_qty: "", unit_selling_price: "" });
        setUoms([]); setBaseUom("PCS"); setSellAvail(null);
      }
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const startEdit = (it) => {
    setEditId(it.id);
    setItem({ product_id: it.product_id, uom_code: it.uom_code, uom_qty: String(it.uom_qty), unit_selling_price: String(it.unit_selling_price) });
    loadUoms(it.product_id, true); loadAvail(it.product_id);
  };
  const cancelEdit = () => { setEditId(null); setItem({ product_id: "", uom_code: "", uom_qty: "", unit_selling_price: "" }); setUoms([]); setBaseUom("PCS"); setSellAvail(null); };

  const deleteItem = async (it) => {
    setError("");
    if (!window.confirm(`Hapus ${it.sku_code} dari draft penjualan?\n${it.uom_qty} ${it.uom_code} (= ${it.base_qty} ${it.base_uom}) × ${idr(it.unit_selling_price)} akan dihapus.\nStok inventory belum berubah karena transaksi belum diposting.`)) return;
    try {
      const { data } = await api.delete(`/inventory/manual-sales/${current.id}/items/${it.id}`);
      setCurrent(data); if (editId === it.id) cancelEdit();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const postSale = async () => {
    setError("");
    const items = current.items || [];
    const lines = items.map((i) => {
      const belowNote = isOwner && i.below_average_cost ? " ⚠ di bawah rata-rata cost" : "";
      return `• ${i.sku_code}: ${i.uom_qty} ${i.uom_code} → kurangi stok ${i.base_qty} ${i.base_uom}${belowNote}`;
    }).join("\n");
    if (!window.confirm(`Penjualan manual akan mengurangi stok GOOD (satuan dasar).\nTransaksi ini belum mencatat penerimaan uang ke Finance.\n\n${lines}\n\nTotal penjualan: ${idr(current.sales_total)}`)) return;
    try { const { data } = await api.post(`/inventory/manual-sales/${current.id}/post`); setCurrent(data); loadList(); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (user && !canView) {
    return <div><AppTopBar active="sales" /><main className="page"><p className="hint">Akses ditolak.</p></main></div>;
  }

  const isDraft = current && current.status === "DRAFT";
  const conv = (() => {
    if (!item.uom_code) return 1;
    if (item.uom_code === baseUom) return 1;
    const u = uoms.find((x) => x.uom_code === item.uom_code);
    return u ? u.conversion_to_base : 1;
  })();
  const q = item.uom_qty === "" ? null : parseInt(item.uom_qty, 10);
  const baseQty = (q != null && !Number.isNaN(q)) ? q * conv : null;
  const avail = sellAvail;
  const over = (avail != null && baseQty != null && baseQty > avail);
  const preview = (item.uom_qty && item.unit_selling_price) ? parseInt(item.uom_qty, 10) * parseFloat(item.unit_selling_price) : 0;

  return (
    <div>
      <AppTopBar active="sales" />
      <main className="page" style={{ maxWidth: 1320, margin: "0 auto" }}>
        <div className="page-head"><h1 className="page-title">Manual Sales</h1>
          {!current && <button className="btn-primary" onClick={createDraft} data-testid="sale-create-button">Buat Penjualan Baru</button>}
        </div>
        {error && <div className="alert-error" data-testid="sale-error">{error}</div>}

        {!current && (
          <>
            <section className="panel" data-testid="sale-newhead-panel">
              <h2 className="panel-title">Detail Penjualan Baru</h2>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
                <div style={{ width: 200 }}><label className="field-label">Channel</label>
                  <select className="field-input" value={head.channel} onChange={(e) => setHead({ ...head, channel: e.target.value })} data-testid="sale-channel-select">
                    {CHANNELS.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                  </select>
                </div>
                <div style={{ width: 260 }}><label className="field-label">Konsumen</label>
                  <select className="field-input" value={head.customer_id} onChange={(e) => setHead({ ...head, customer_id: e.target.value })} data-testid="sale-customer-select">
                    <option value="">Umum / Walk-in</option>
                    {customers.map((c) => <option key={c.id} value={c.id}>{c.customer_code} — {c.customer_name}</option>)}
                  </select>
                </div>
                {!head.customer_id && (
                  <div style={{ width: 220 }}><label className="field-label">Nama Walk-in (opsional)</label>
                    <input className="field-input" value={head.customer_name} onChange={(e) => setHead({ ...head, customer_name: e.target.value })} data-testid="sale-customer-input" />
                  </div>
                )}
                <div style={{ width: 220 }}><label className="field-label">Catatan (opsional)</label>
                  <input className="field-input" value={head.note} onChange={(e) => setHead({ ...head, note: e.target.value })} data-testid="sale-note-input" />
                </div>
              </div>
            </section>
            <section className="panel" data-testid="sale-list-panel">
              <h2 className="panel-title">Daftar Penjualan ({list.length})</h2>
              <div className="table-scroll">
                <table className="data-table" data-testid="sale-list-table">
                  <thead><tr><th>Sale No</th><th>Channel</th><th>Status</th><th>Tanggal</th><th></th></tr></thead>
                  <tbody>
                    {list.length === 0 && <tr><td colSpan={5} className="empty-cell">Belum ada penjualan.</td></tr>}
                    {list.map((s) => (
                      <tr key={s.id} data-testid={`sale-row-${s.sale_number}`}>
                        <td className="mono">{s.sale_number}</td>
                        <td>{channelLabel(s.channel)}</td>
                        <td><span className={`status-pill ${s.status === "POSTED" ? "on" : "off"}`}>{s.status}</span></td>
                        <td>{new Date(s.created_at).toLocaleString("id-ID")}</td>
                        <td><button className="btn-mini" onClick={() => openSale(s.id)} data-testid={`sale-open-${s.sale_number}`}>Buka</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}

        {current && (
          <section className="panel" data-testid="sale-detail-panel">
            <div className="page-head">
              <h2 className="panel-title">{current.sale_number} · {channelLabel(current.channel)} — <span className={`status-pill ${current.status === "POSTED" ? "on" : "off"}`} data-testid="sale-status">{current.status}</span></h2>
              <button className="btn-ghost" onClick={() => { setCurrent(null); cancelEdit(); loadList(); }} data-testid="sale-back-button">Kembali</button>
            </div>
            {current.customer_name && <p className="hint">Customer: {current.customer_name}</p>}

            <div className="panel" style={{ marginTop: 8 }} data-testid="sale-customer-panel">
              {isDraft ? (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
                  <div style={{ width: 300 }}><label className="field-label">Konsumen</label>
                    <select className="field-input" value={current.customer_id || ""} onChange={(e) => changeCustomer(e.target.value)} data-testid="sale-detail-customer-select">
                      <option value="">Umum / Walk-in</option>
                      {customers.map((c) => <option key={c.id} value={c.id}>{c.customer_code} — {c.customer_name}</option>)}
                    </select>
                  </div>
                </div>
              ) : (
                <div className="field-label">Konsumen</div>
              )}
              {current.customer_id ? (() => {
                const c = customers.find((x) => x.id === current.customer_id);
                return (
                  <div className="hint" style={{ marginTop: 6 }} data-testid="sale-customer-summary">
                    <strong>{current.customer_name_snapshot || (c && c.customer_name)}</strong>
                    {c && <> · {CTYPE[c.customer_type] || c.customer_type}</>}
                    {c && c.pic_name && <> · PIC: {c.pic_name}</>}
                    {(current.customer_phone_snapshot || (c && (c.whatsapp || c.phone))) && <> · WA/Telp: {current.customer_phone_snapshot || (c && (c.whatsapp || c.phone))}</>}
                    {current.customer_shipping_address_snapshot && <><br />Alamat kirim: {current.customer_shipping_address_snapshot}</>}
                  </div>
                );
              })() : (
                <div className="hint" style={{ marginTop: 6 }} data-testid="sale-customer-summary">{current.customer_display || "Umum / Walk-in"}</div>
              )}
            </div>

            {isDraft && (
              <div className="panel" style={{ marginTop: 8 }} data-testid="sale-additem-form">
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
                  <div style={{ flex: "1 1 260px", minWidth: 0 }} data-testid="sale-product-select"><label className="field-label">SKU</label>
                    <SkuCombobox products={products} value={item.product_id || null} disabled={!!editId} onChange={onPickProduct} index="sale" />
                  </div>
                  <div style={{ width: 150 }}><label className="field-label">Satuan</label>
                    <select className="field-input" value={item.uom_code} disabled={!item.product_id} onChange={(e) => setItem({ ...item, uom_code: e.target.value })} data-testid="sale-uom-select">
                      {uoms.length === 0 && <option value={baseUom}>{baseUom}</option>}
                      {uoms.map((u) => <option key={u.uom_code} value={u.uom_code}>{uomLabel(u)}</option>)}
                    </select>
                  </div>
                  <div style={{ width: 90 }}><label className="field-label">Qty</label>
                    <input className="field-input" type="number" min="1" value={item.uom_qty} onChange={(e) => setItem({ ...item, uom_qty: e.target.value })} data-testid="sale-qty-input" />
                  </div>
                  <div style={{ width: 150 }}><label className="field-label">Harga Jual / Satuan</label>
                    <input className="field-input" type="number" min="0" step="0.01" value={item.unit_selling_price} onChange={(e) => setItem({ ...item, unit_selling_price: e.target.value })} data-testid="sale-price-input" />
                  </div>
                  <div style={{ width: 140 }}><label className="field-label">Subtotal</label>
                    <div className="field-input" style={{ opacity: 0.7, pointerEvents: "none" }} data-testid="sale-subtotal-preview">{idr(preview)}</div>
                  </div>
                  <button className="btn-primary" onClick={addItem} data-testid="sale-additem-button">{editId ? "Simpan Perubahan" : "Tambah Item"}</button>
                  {editId && <button className="btn-ghost" onClick={cancelEdit} data-testid="sale-canceledit-button">Batal Edit</button>}
                </div>
                {item.product_id && (
                  <div className="hint" style={{ marginTop: 6 }} data-testid="sale-uom-context">
                    {conv > 1 ? `1 ${item.uom_code} = ${conv} ${baseUom}` : `Satuan dasar: ${baseUom}`}
                    {baseQty != null && <> · Qty transaksi {q} {item.uom_code} = <strong>{baseQty} {baseUom}</strong></>}
                    {avail != null && <> · Stok GOOD tersedia: <strong data-testid="sale-stock-avail">{avail} {baseUom}</strong></>}
                  </div>
                )}
                {over && <div className="alert-error" style={{ marginTop: 8 }} data-testid="sale-stock-warning">Qty konversi ({baseQty} {baseUom}) melebihi stok GOOD saat ini ({avail} {baseUom}). Item dapat disimpan sebagai draft, tetapi penjualan tidak dapat diposting sebelum stok mencukupi.</div>}
              </div>
            )}

            <div className="table-scroll" style={{ marginTop: 8 }}>
              <table className="data-table" data-testid="sale-items-table">
                <thead><tr><th>SKU</th><th>Deskripsi</th><th>Qty / Satuan</th><th>Konversi / Kurangi Stok</th><th>Harga Jual / Satuan</th><th>Subtotal</th>{isOwner && <th>Cost Basis</th>}{isDraft && <th>Aksi</th>}</tr></thead>
                <tbody>
                  {(current.items || []).length === 0 && <tr><td colSpan={6 + (isOwner ? 1 : 0) + (isDraft ? 1 : 0)} className="empty-cell">Belum ada item.</td></tr>}
                  {(current.items || []).map((it) => (
                    <tr key={it.id} data-testid={`sale-item-${it.sku_code}`}>
                      <td className="mono">{it.sku_code}</td>
                      <td>{it.description}</td>
                      <td className="mono" data-testid={`sale-item-uom-${it.sku_code}`}>{it.uom_qty} {it.uom_code}</td>
                      <td className="mono" data-testid={`sale-item-base-${it.sku_code}`}>{it.conversion_to_base_snapshot > 1 ? `${it.conversion_to_base_snapshot}× → ` : ""}{it.base_qty} {it.base_uom}</td>
                      <td className="mono">{idr(it.unit_selling_price)}</td>
                      <td className="mono">{idr(it.line_total)}
                        {isOwner && it.below_average_cost && <span className="status-pill off" style={{ marginLeft: 6 }} data-testid={`sale-item-belowcost-${it.sku_code}`}>di bawah cost</span>}
                      </td>
                      {isOwner && <td className="mono">{it.inventory_cost_basis != null ? idr(it.inventory_cost_basis) : "-"}</td>}
                      {isDraft && (
                        <td className="actions-cell">
                          <button className="btn-mini" onClick={() => startEdit(it)} data-testid={`sale-edit-${it.sku_code}`}>Edit</button>
                          <button className="btn-mini" onClick={() => deleteItem(it)} data-testid={`sale-delete-${it.sku_code}`}>Hapus</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="panel-title" style={{ marginTop: 10 }} data-testid="sale-total">Total Penjualan: {idr(current.sales_total)}</p>

            {isDraft && (
              <div className="form-actions" style={{ marginTop: 10 }}>
                <button className="btn-primary" onClick={postSale} disabled={(current.items || []).length === 0} data-testid="sale-post-button">Posting Penjualan</button>
              </div>
            )}
            {!isDraft && (
              <div className="form-actions" style={{ marginTop: 10 }}>
                <button className="btn-primary" onClick={openOrCreateDO} data-testid="sale-do-button">
                  {current.delivery_order_id ? "Lihat / Cetak Surat Jalan" : "Generate Surat Jalan"}
                </button>
                <button className="btn-primary" onClick={openOrCreateInvoice} data-testid="sale-invoice-button">
                  {current.invoice_id ? "Lihat / Cetak Invoice" : "Generate Invoice"}
                </button>
              </div>
            )}

            {!isDraft && isOwner && pay && (
              <div className="panel" style={{ marginTop: 12 }} data-testid="sale-payment-panel">
                <div className="panel-head">
                  <h2 className="panel-title">Pembayaran</h2>
                  <span className={`status-pill ${pay.payment_status === "PAID" ? "on" : "off"}`} data-testid="sale-payment-status">{pay.payment_status}</span>
                </div>
                <div className="meta-grid">
                  <div><span className="meta-label">Total Penjualan</span><span className="mono" data-testid="cpay-total">{idr(pay.sales_total)}</span></div>
                  <div><span className="meta-label">Total Dibayar</span><span className="mono" data-testid="cpay-paid">{idr(pay.total_paid)}</span></div>
                  <div><span className="meta-label">Sisa (Outstanding)</span><span className="mono" data-testid="cpay-outstanding">{idr(pay.outstanding)}</span></div>
                </div>

                {pay.payment_status !== "PAID" && !showPayForm && (
                  <div className="form-actions">
                    <button className="btn-primary" onClick={() => setShowPayForm(true)} data-testid="record-payment-button">Catat Pembayaran</button>
                  </div>
                )}

                {showPayForm && (
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 10, flexWrap: "wrap" }} data-testid="payment-form">
                    <div><label className="field-label">Nominal</label>
                      <input className="field-input narrow" type="number" min="1" step="0.01" placeholder="jumlah" value={payForm.amount}
                        onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="payment-amount-input" /></div>
                    <div><label className="field-label">Account Penerima</label>
                      <select className="field-input" value={payForm.account_id}
                        onChange={(e) => setPayForm({ ...payForm, account_id: e.target.value })} data-testid="payment-account-select" style={{ minWidth: 220 }}>
                        <option value="">-- pilih account --</option>
                        {accounts.filter((a) => a.is_active).map((a) => (
                          <option key={a.account_id} value={a.account_id}>{a.account_name} ({a.account_type}) — {idr(a.current_balance)}</option>
                        ))}
                      </select></div>
                    <div><label className="field-label">Referensi / Catatan</label>
                      <input className="field-input" placeholder="opsional" value={payForm.reference}
                        onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} data-testid="payment-reference-input" style={{ maxWidth: 220 }} /></div>
                    <button className="btn-primary" onClick={recordPayment} data-testid="payment-submit-button">Simpan</button>
                    <button className="btn-ghost" onClick={() => setShowPayForm(false)} data-testid="payment-cancel-button">Batal</button>
                  </div>
                )}

                <div className="table-scroll" style={{ marginTop: 12 }}>
                  <table className="data-table" data-testid="payment-history-table">
                    <thead><tr><th>Tanggal</th><th>Account</th><th>Nominal</th><th>Referensi</th><th>User</th></tr></thead>
                    <tbody>
                      {pay.payments.length === 0 && <tr><td colSpan={5} className="empty-cell" data-testid="payments-empty">Belum ada pembayaran.</td></tr>}
                      {pay.payments.map((m) => (
                        <tr key={m.id} data-testid={`payment-row-${m.id}`}>
                          <td>{new Date(m.payment_date).toLocaleDateString("id-ID")}</td>
                          <td>{m.account_name || "-"}</td>
                          <td className="mono">{idr(m.amount)}</td>
                          <td>{m.reference || "-"}</td>
                          <td className="mono">{m.created_by ? m.created_by.slice(0, 8) : "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
